/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { createStore, mutatorAction } from "satcheljs";

import { CreateGenericStoreParams } from "./CreateGenericStoreParams";
import { CreateGenericStoreResult } from "./CreateGenericStoreResult";
import { WithExtraActions } from "./WithExtraActions";

/**
 * Creates a generic store by composing given actions and states.
 * @param params The parameters to create the generic store.
 * @returns The created generic store and its actions.
 */
export function createGenericStore<TState, TActions>(
    params: CreateGenericStoreParams<TState, TActions>
): CreateGenericStoreResult<TState, WithExtraActions<TActions>> {
    const { actionsCreators, namespace, statesCreators } = params;

    const getInitialState = (): TState => statesCreators.reduce(
        (acc, stateCreator) => Object.assign(acc, stateCreator()),
        // TODO: Find how to remove casting.
        {} as unknown as TState
    );

    const store = createStore<TState>(namespace, getInitialState());

    const actions = actionsCreators.reduce(
        (acc, actionCreator) => Object.assign(acc, actionCreator({ namespace, store })),
        // TODO: Find how to remove casting.
        {} as unknown as WithExtraActions<TActions>
    );

    actions.reset = mutatorAction(`${namespace}Reset`, () => {
        // Creates new initial state to ensure arrays and ObservableMap(s) are new references.
        const initialState = getInitialState();

        Object.keys(initialState).forEach(key => {
            // TODO: Find how to remove casting.
            const stateKey: keyof TState = key as keyof TState;

            store()[stateKey] = initialState[stateKey];
        });
    });

    return {
        actions,
        store
    };
}
